import { useState } from "preact/compat"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { MailboxProvider, provider_display_name } from "@/api/mailbox_invite"
import { MailboxConnectPanel } from "./mailbox_connect_panel"

interface ConnectMailboxButtonsProps {
  /** Where to return after connecting. Defaults to the current page. */
  next?: string
  /** Called after the dialog closes, so the page can reload its connected-account list. */
  onChange?: () => void
  gmailLabel?: string
  microsoftLabel?: string
  /** Pre-fills the mailbox address in the dialog, for re-connecting a mailbox we already named. */
  defaultEmail?: string
  className?: string
}

/**
 * The one way to add a mailbox from inside the app. Every page that used to send the user straight
 * to the provider now goes through here, so the mailbox is always named first and the callback can
 * refuse anything else — see `db::mailbox_invite`.
 */
export function ConnectMailboxButtons({
  next,
  onChange,
  gmailLabel = "Connect Gmail",
  microsoftLabel = "Connect Microsoft 365",
  defaultEmail,
  className,
}: ConnectMailboxButtonsProps) {
  const [provider, setProvider] = useState<MailboxProvider | null>(null)

  function close() {
    setProvider(null)
    onChange?.()
  }

  return (
    <div class={className}>
      <div class="space-y-3">
        <Button
          variant="outline"
          class="w-full flex items-center justify-center gap-2"
          onClick={() => setProvider("gmail")}>
          <img src={gmailLogo} alt="Gmail" class="h-5" />
          <span>{gmailLabel}</span>
        </Button>
        <Button
          variant="outline"
          class="w-full flex items-center justify-center gap-2"
          onClick={() => setProvider("microsoft")}>
          <img src={microsoftLogo} alt="Microsoft" class="h-5" />
          <span>{microsoftLabel}</span>
        </Button>
      </div>

      <Dialog open={provider !== null} onOpenChange={open => !open && close()}>
        <DialogContent class="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect {provider ? provider_display_name(provider) : ""}</DialogTitle>
            <DialogDescription>
              Name the mailbox your deductions and check remittances arrive in — usually something
              like ap@yourcompany.com or deductions@yourcompany.com.
            </DialogDescription>
          </DialogHeader>
          <div class="space-y-4 text-sm text-gray-700">
            {provider && (
              <MailboxConnectPanel
                provider={provider}
                next={next ?? window.location.pathname}
                onConnected={close}
                defaultEmail={defaultEmail}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
